import React, { useState } from 'react'
import { CreditDocument } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import FileUpload from '@divvy-web/skylab.fileupload'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import { useTrackCollaboratorEvent } from '@sputnik/hooks/useApplicationCollaborators'
import { getDocumentTypeForTracking } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/DocumentsTabContent/trackingUtils'
import {
  GetCreditApplicationDetails,
  GetCreditApplicationDetailsDocument,
  GetCreditApplicationDetailsVariables,
} from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/GetCreditApplicationDetails.gql'
import { useFileUploadStatus } from '@sputnik/pages/DashboardPages/FileUploadStatusProvider'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logWarning } from '@sputnik/utils/loggerUtils'

import { useGenerateRequestedDocUploadUrl } from './GenerateRequestedDocUploadUrl.gql'
import SelectDocumentTypeDrawer from './SelectDocumentTypeDrawer'
import styles from './styles.module.scss'

export interface FileUploadSectionProps {
  salesforceCreditId: string
}

const FileUploadSection = ({ salesforceCreditId }: FileUploadSectionProps) => {
  const [getClassName, makeTestId] = useNameFormatter('FileUploadSection')
  const showToast = useToast()
  const { addNewFileUpload } = useFileUploadStatus()
  const { track } = useWebTracking()

  const { trackCollaboratorEvent } = useTrackCollaboratorEvent({
    salesforceCreditId,
  })

  const trackDocumentUploadSubmission = (documentType: CreditDocument) => {
    trackCollaboratorEvent('se_signup__application_dashboard_documents_tab_modal__submitted', (role) => ({
      person_type: role,
      se_dashboard_document_type: getDocumentTypeForTracking(documentType),
    }))
  }

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [generateSignedDocumentUploadUrl, { client }] = useGenerateRequestedDocUploadUrl()

  const onDocumentTypeConfirmation = async (documentType: CreditDocument) => {
    if (!selectedFile) {
      return
    }

    const uploadErrorMessage = (
      <FormattedMessage
        defaultMessage='Upload error. Try again'
        id='sputnik.FileUploadSection__guqnep'
      />
    )

    try {
      const { data } = await generateSignedDocumentUploadUrl({
        variables: {
          creditApplicationId: salesforceCreditId,
          documentType,
          fileName: selectedFile.name,
        },
      })

      if (!data?.docUploadInfo?.url) {
        logWarning('generateSignedDocumentUploadUrl', {
          attributes: {
            creditAppId: salesforceCreditId,
            result: `No document upload URL returned: ${documentType}`,
          },
          eventName: 'UploadDocument',
        })
        showToast(TOAST_TYPE_DANGER, uploadErrorMessage, { className: 'dd-privacy-allow' })
        return
      }

      const options = {
        headers: {
          'Content-Disposition': 'attachment',
          'Content-Type': selectedFile.type,
        },
      }

      try {
        await fetch(data?.docUploadInfo?.url, {
          method: 'PUT',
          body: selectedFile,
          ...options,
        })

        addNewFileUpload({
          salesforceCreditId,
          fileName: selectedFile.name,
          documentType: documentType,
          documentUuid: data?.docUploadInfo?.documentUuid ?? '',
        })
        trackDocumentUploadSubmission(documentType)
        client.cache.updateQuery<GetCreditApplicationDetails, GetCreditApplicationDetailsVariables>(
          {
            query: GetCreditApplicationDetailsDocument,
            variables: {
              creditApplicationId: salesforceCreditId,
            },
          },
          (prev) => {
            if (!prev?.creditApplication?.supportingDocuments || !prev.creditApplication.requiredDocs) {
              return
            }

            const requiredDocumentIndex = prev.creditApplication.requiredDocs.findIndex(
              (doc) => doc.documentType === documentType,
            )

            if (requiredDocumentIndex >= 0) {
              const supportingDocumentIndex = prev.creditApplication.supportingDocuments.findIndex(
                (doc) => doc.documentType === documentType,
              )

              const updatedRequiredDocumentRecord = {
                ...prev.creditApplication.requiredDocs[requiredDocumentIndex],
                documentUuid: data?.docUploadInfo?.documentUuid ?? '',
                fileName: selectedFile.name,
                signedUrl: data?.docUploadInfo?.url,
              }
              return {
                ...prev,
                creditApplication: {
                  ...prev.creditApplication,
                  requiredDocs: [
                    ...prev.creditApplication.requiredDocs.slice(0, requiredDocumentIndex),
                    updatedRequiredDocumentRecord,
                    ...prev.creditApplication.requiredDocs.slice(requiredDocumentIndex + 1),
                  ],
                  supportingDocuments: [
                    ...prev.creditApplication.supportingDocuments.slice(0, supportingDocumentIndex),
                    updatedRequiredDocumentRecord,
                    ...prev.creditApplication.supportingDocuments.slice(supportingDocumentIndex + 1),
                  ],
                },
              }
            }

            return {
              ...prev,
              creditApplication: {
                ...prev.creditApplication,
                supportingDocuments: [
                  {
                    __typename: 'CreditApplicationDocument',
                    documentType,
                    documentUuid: data?.docUploadInfo?.documentUuid ?? '',
                    fileName: selectedFile.name,
                    isReRequested: false,
                    unsignedPath: null,
                    signedUrl: data?.docUploadInfo?.url,
                    status: null,
                    statusMessage: '',
                  },
                  ...prev.creditApplication.supportingDocuments,
                ],
              },
            }
          },
        )
      } catch (e) {
        logError(e, {
          attributes: {
            creditAppId: salesforceCreditId,
            action: 'uploadToS3',
            result: e,
          },
          eventName: 'UploadDocument',
        })
        showToast(TOAST_TYPE_DANGER, uploadErrorMessage, { className: 'dd-privacy-allow' })
      }
    } catch (e) {
      logError(e, {
        attributes: {
          creditAppId: salesforceCreditId,
          action: 'generateSignedDocumentUploadUrl',
          result: e,
        },
        eventName: 'UploadDocument',
      })
      showToast(TOAST_TYPE_DANGER, uploadErrorMessage, { className: 'dd-privacy-allow' })
    } finally {
      setSelectedFile(null)
    }
  }

  // since we have this configured for "no multi file uploads"
  // this will always return 1 file when called
  const onFileSelected = async (files: File[]) => {
    track('se_signup__application_dashboard_documents_tab_upload_doc__started')
    setSelectedFile(files[0])
  }

  return (
    <div className={styles[getClassName('')]}>
      <div className={styles[getClassName('title')]}>
        <FormattedMessage
          defaultMessage='Upload documents'
          id='sputnik.FileUploadSection__MKbPrT'
        />
      </div>
      <FileUpload
        acceptedFileTypes={['.pdf', '.jpg', '.jpeg', '.png', '.csv', '.xls', '.xlsx']}
        canUploadMultiple={false}
        captionEmptyState={
          <FormattedMessage
            defaultMessage='upload one'
            id='sputnik.FileUploadSection__HF3pSw'
          />
        }
        className={styles[getClassName('file-upload-area')]}
        dataTestId={makeTestId('file-upload-area')}
        // I believe we do not want to show the files here
        // but this field is required
        files={[]}
        onFilesChange={onFileSelected}
      />
      <SelectDocumentTypeDrawer
        isShowing={selectedFile !== null}
        salesforceCreditId={salesforceCreditId}
        onCancel={() => setSelectedFile(null)}
        onDocumentTypeConfirmation={onDocumentTypeConfirmation}
      />
    </div>
  )
}

export default FileUploadSection
